import React, { FunctionComponent, Suspense, useState } from 'react';
import { HashRouter, Redirect, Switch } from 'react-router-dom';
import { RecoilRoot, useRecoilValue } from 'recoil';
import { HaperProvider } from 'haper-hooks';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { PublicRoute } from './components/PublicRoute/PublicRoute';
import {
    routing,
    modules,
    RoutingItem,
    getDefaultRedirectPathForRole,
} from './config/routing';
import { Layout } from './components/Layout/Layout';
import { Menu } from './components/Menu/Menu';
import { NavBar } from './components/NavBar/NavBar';
import { progressBarVisibility } from './shared/states/globalAppProgressBar';
import { haper } from './shared/haper';
import { SuspenseStateTransmitter } from './components/SuspenseStateTransmitter/SuspenseStateTransmitter';
import { AsyncMountLoader } from './components/PageLoader/AsyncMountLoader';
import { session } from './shared/session';
import { BackCountHelper } from './components/BackButton/BackButton';


//@ts-ignore
(window as any).toastr = {error: console.log,success: console.log,info: console.log};

const NestedRoutesRenderer: FunctionComponent<{
    path: string;
    routes: Array<RoutingItem>;
}> = ({ path, routes }) => {
    return (
        <Switch>
            {routes.map((route) => (
                <ProtectedRoute
                    path={`${path}${route.path}`}
                    authorizedRoles={route.authorizedRoles}
                    exact={route.exact}
                    key={route.path}
                >
                    {route.Cmp && <route.Cmp />}
                </ProtectedRoute>
            ))}
        </Switch>
    );
};

const ProtectedAppSection = () => {
    const progressBarVisible = useRecoilValue(progressBarVisibility);

    return (
        <Layout
            menu={<Menu routes={routing} />}
            navBar={<NavBar routes={routing} />}
            progressBarLoading={progressBarVisible}
            progressBarVisible={true}
        >
            <Switch>
                <ProtectedRoute
                    render={() => (
                        <Redirect to={getDefaultRedirectPathForRole(session)} />
                    )}
                    path="/"
                    exact
                />
                {routing.map((route) => (
                    <ProtectedRoute
                        key={route.path}
                        authorizedRoles={route.authorizedRoles}
                        path={route.path}
                    >
                        {route.children ? (
                            <NestedRoutesRenderer
                                path={route.path}
                                routes={route.children}
                            />
                        ) : (
                            route.Cmp && <route.Cmp />
                        )}
                    </ProtectedRoute>
                ))}
                <Redirect to={modules.Unauthorized.path} />
            </Switch>
        </Layout>
    );
};

function App() {
    const [loading, setLoading] = useState<boolean>(false);

    return (
        <RecoilRoot>
            <HaperProvider haperInstance={haper}>
                <>
                    <HashRouter>
                        <BackCountHelper />
                        <AsyncMountLoader
                            entity={loading}
                            showLoaderContent
                            minHeight="100vh"
                        >
                            <Suspense
                                fallback={
                                    <SuspenseStateTransmitter
                                        setLoading={setLoading}
                                    />
                                }
                            >
                                <Switch>
                                    <ProtectedRoute
                                        path={modules.Login.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.Login.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.ThanksForRegister.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.ThanksForRegister.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.ActivateAccount.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.ActivateAccount.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.Register.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.Register.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.ResetPassword.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.ResetPassword.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.SetNewPassword.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.SetNewPassword.Cmp}
                                    </ProtectedRoute>
                                    <PublicRoute
                                        path={modules.Contact.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.Contact.Cmp}
                                    </PublicRoute>
                                    <PublicRoute
                                        path={modules.News.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.News.Cmp}
                                    </PublicRoute>
                                    
                                    <ProtectedRoute
                                        path={modules.DeclarationOfAvailability.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.DeclarationOfAvailability.Cmp}
                                    </ProtectedRoute>
                                    <ProtectedRoute
                                        path={modules.PrivacyPolicy.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.PrivacyPolicy.Cmp}
                                    </ProtectedRoute>
                                    <PublicRoute
                                        path={modules.InformationClause.path}
                                        redirectTo="/"
                                        exact
                                        invert
                                    >
                                        {modules.InformationClause.Cmp}
                                    </PublicRoute>

                                    <ProtectedRoute
                                        path="/"
                                        redirectTo="/login"
                                    >
                                        <ProtectedAppSection />
                                    </ProtectedRoute>
                                </Switch>
                            </Suspense>
                        </AsyncMountLoader>
                    </HashRouter>
                </>
            </HaperProvider>
        </RecoilRoot>
    );
}

export default App;
